import { useState, useCallback, useRef } from "react";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Row, Col, Select as AntSelect } from "antd";
import { Form, Cascader, Input } from "formik-antd";
import { useTranslation } from "react-i18next";
import {
  Report as ReportType,
  ReportColumn as ReportColumnT,
  ReportColumnType,
} from "/app/src/models";
import { reportColumnService } from "/app/src/services";
import FormikDisabler from "/app/src/components/generic/components/formikDisabler";
import DisabledSubmitButton from "/app/src/components/generic/components/buttons/DisabledSubmitButton";
import { useAccessContext } from "/app/src/contexts/hooks/useAccessContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import { cascadeReportColumnTypes } from "/app/src/helpers/cascadeReportColumnTypes";
import Comparison from "./comparison";

interface FormValues {
  tableColumn: [string, number][] | undefined;
  name: string | undefined;
  reportId?: number;
  firstColumn: number | undefined;
  secondColumn: number | undefined;
  filteringValue: string | undefined;
  position?: string;
}

/**
 * Filters the given array of options based on the specified input value.
 * @param {string} inputValue - The string to search for within each option's label.
 * @param {any[]} path - The array of options where each option has a label property.
 * @returns {boolean} True if at least one option's label contains the input value (case insensitive), otherwise false.
 */
function filter(inputValue: string, path: any[]) {
  return path.some(
    (option) =>
      option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
  );
}

/**
 * Creates a new report column.
 * @param {Object} params - The function parameters.
 * @param {ReportType} params.report - The current report.
 * @param {ReportColumnType[]} params.reportColumnTypes - The list of report column types.
 * @param {ReportColumnT[]} params.columns - The existing report columns.
 * @returns {JSX.Element} The new report column component.
 */
export default function NewReportColumn({
  report,
  reportColumnTypes,
  columns,
}: {
  report: ReportType;
  reportColumnTypes: ReportColumnType[];
  columns: ReportColumnT[];
}) {
  const { t } = useTranslation();
  const { canEdit } = useAccessContext();
  const queryClient = useQueryClient();
  const [newColumnType, setNewColumnType] = useState<
    "column" | "comparison" | "count"
  >("column");
  const namePlaceholderRef = useRef<string>(t("translation:enter_name"));

  /**
   * Formats the form values into a report column.
   * @param {FormValues} values - The form values.
   * @returns {ReportColumnT} The formatted report column.
   */
  const formatForm = useCallback(
    (values: FormValues): ReportColumnT => {
      if (!values.name) {
        // Derive a sensible default name when creating comparison columns
        if (newColumnType === "comparison") {
          const first = columns.find((c) => c.id === values.firstColumn);
          const second = columns.find((c) => c.id === values.secondColumn);
          const opKey = values.filteringValue as
            | "percentage"
            | "ratio"
            | "sum"
            | "product"
            | "modulus"
            | "difference"
            | undefined;
          const opLabelMap: Record<string, string> = {
            percentage: t("translation:percentage"),
            ratio: t("translation:ratio"),
            sum: t("translation:sum"),
            product: t("translation:product"),
            modulus: t("translation:modulus"),
            difference: t("translation:difference"),
          };

          let opLabel = opKey ? opLabelMap[opKey] : t("translation:comparison");
          if (opLabel === undefined) {
            opLabel = "-";
          }
          if (first?.name && second?.name) {
            values.name = `${first.name} ${opLabel} ${second.name}`;
          } else {
            // fallback to generic prompt if incomplete selections
            values.name = t("translation:enter_name");
          }
        } else {
          // if no name, use the placeholder value as the name
          values.name = namePlaceholderRef.current;
        }
      }
      const ret: ReportColumnT = {
        name: values.name,
        reportId: values.reportId,
        reportColumnTypeId: undefined,
        type: newColumnType,
        filteringValue: values.filteringValue,
        position: values.position,
      };
      if (newColumnType === "column" && values?.tableColumn) {
        ret.reportColumnTypeId = values.tableColumn[0][1];
      }
      if (newColumnType === "comparison") {
        if (values.firstColumn) {
          if (values.firstColumn !== 0) {
            ret.firstColumn = values.firstColumn;
          }
        }
        if (values.secondColumn) {
          if (values.secondColumn !== 0) {
            ret.secondColumn = values.secondColumn;
          }
        }
      }
      return ret;
    },
    [newColumnType, columns, t],
  );

  /**
   * Creates a new column using the report column service.
   * @param {ReportColumnT} values - The column values.
   * @returns {Promise<any>} Promise with the created column data.
   */
  const createColumn = useCallback((values: ReportColumnT) => {
    const { parentIds: _parentIds, ...createValues } = values;
    return reportColumnService
      .createSingle(createValues)
      .then(handlePromiseError);
  }, []);

  const { mutateAsync: addColumn } = useMutation({
    mutationFn: (values: ReportColumnT) => {
      return createColumn(values);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["reportColumns", report.id],
        (oldData: { report_columns: ReportColumnT[] }) => {
          return {
            report_columns: [...oldData.report_columns, data.report_column],
          };
        },
      );
    },
  });

  /**
   * Calculates a default name for the column.
   * @param {number} reportColumnTypeId - The report column type id.
   * @returns {string} The calculated default name.
   */
  const calculateDefaultName = useCallback(
    (reportColumnTypeId: number) => {
      const col = reportColumnTypes.find((c) => c.id === reportColumnTypeId);
      let updatedName = "";
      if (col?.finalTable === "Variance") {
        updatedName = `${col.integrationName} ${col.name}`;
      } else if (col?.baseTable !== col?.finalTable) {
        updatedName = `${col.finalTable} ${col.name}`;
      } else {
        updatedName = col?.name || "";
      }
      return updatedName;
    },
    [reportColumnTypes],
  );

  /**
   * Handles changes in the cascader selection.
   * @param {Array<[string, number]>} values - Selected cascader values.
   */
  const handleCascaderChange = useCallback(
    (values: [string, number][]) => {
      if (values.length === 1) {
        namePlaceholderRef.current = calculateDefaultName(values[0][1]);
      } else {
        namePlaceholderRef.current = t("translation:enter_name");
      }
    },
    [calculateDefaultName, t],
  );

  /**
   * Handles the submission of adding a new column.
   * @param {FormValues} values - Form values for the new column.
   * @param {FormikHelpers<FormValues>} actions - Formik helper actions.
   * @returns {Promise<null|Array>} A promise resolving null or an array of created columns.
   */
  const addColumnHandler = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      values.reportId = report.id;
      if (values.tableColumn && values.tableColumn.length > 1) {
        const promises = values.tableColumn.map((entry, index) => {
          const newValues = {
            ...values,
            reportId: report.id,
            tableColumn: [entry],
            position: (columns.length + index).toString(),
          };

          newValues.name = calculateDefaultName(entry[1]);

          const formattedValues = formatForm(newValues);

          return createColumn(formattedValues).then((data) => {
            return data.report_column;
          });
        });

        // Wait for all columns to be added before resetting the form
        return await Promise.all(promises).then((data) => {
          actions.resetForm();
          namePlaceholderRef.current = t("translation:enter_name");
          queryClient.setQueryData(
            ["reportColumns", report.id],
            (oldData: { report_columns: ReportColumnT[] }) => {
              return {
                report_columns: [...oldData.report_columns, ...data],
              };
            },
          );
        });
      } else {
        const newValues = {
          ...values,
          position: columns.length.toString(),
        };
        const formattedValues = formatForm(newValues);
        await addColumn(formattedValues);
      }
      actions.resetForm();
      namePlaceholderRef.current = t("translation:enter_name");
      return null;
    },
    [
      report.id,
      t,
      columns.length,
      calculateDefaultName,
      formatForm,
      createColumn,
      queryClient,
      addColumn,
    ],
  );

  /**
   * Handles the change of column type.
   * When switching to a comparison, reset the name placeholder
   * to a generic prompt instead of any previously computed column name.
   * @param {"column"|"comparison"|"count"} value - The selected column type.
   */
  const handleTypeChange = useCallback(
    (value: "column" | "comparison" | "count") => {
      setNewColumnType(value);
      if (value === "comparison") {
        namePlaceholderRef.current = t("translation:enter_name");
      }
    },
    [t],
  );

  /**
   * Renders the new column form.
   * @param {FormikProps<FormValues>} props - Formik form properties.
   * @returns {JSX.Element} The rendered form component.
   */
  const newColumnForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty, isSubmitting, values }) => (
        <Form>
          <FormikDisabler disabled={!canEdit} />
          <Row justify="start" gutter={16}>
            <Col span={3}>
              <AntSelect
                onChange={handleTypeChange}
                value={newColumnType}
                style={{ width: "100%" }}
                size="large"
              >
                <AntSelect.Option value="column">
                  {t("translation:column")}
                </AntSelect.Option>
                <AntSelect.Option value="comparison">
                  {t("translation:comparison")}
                </AntSelect.Option>
                {report?.groupColumnId && (
                  <AntSelect.Option value="count">
                    {t("translation:count")}
                  </AntSelect.Option>
                )}
              </AntSelect>
            </Col>
            {newColumnType === "column" ? (
              <Col span={11}>
                <Form.Item name="tableColumn">
                  <Cascader
                    name="tableColumn"
                    size="large"
                    className="larger-cascade"
                    options={cascadeReportColumnTypes(
                      reportColumnTypes,
                      report.baseTable,
                    )}
                    onChange={handleCascaderChange}
                    placeholder={t("translation:select_column")}
                    showSearch={{ filter }}
                    multiple
                    showCheckedStrategy="SHOW_CHILD"
                  />
                </Form.Item>
              </Col>
            ) : (
              newColumnType === "comparison" && <Comparison columns={columns} />
            )}
            {((values.tableColumn && values.tableColumn.length < 2) ||
              newColumnType === "count") && (
              <Col span={5}>
                <Form.Item name="name" hasFeedback={false}>
                  <Input
                    name="name"
                    placeholder={namePlaceholderRef.current}
                    size="large"
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={5}>
              <DisabledSubmitButton
                type="primary"
                size="large"
                block
                disabled={!dirty || isSubmitting}
              >
                {t("translation:add_column")}
              </DisabledSubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [
        canEdit,
        handleTypeChange,
        newColumnType,
        t,
        report?.groupColumnId,
        report.baseTable,
        reportColumnTypes,
        handleCascaderChange,
        columns,
      ],
    );
  return (
    <Formik
      component={newColumnForm}
      initialValues={{
        tableColumn: undefined,
        name: undefined,
        firstColumn: undefined,
        secondColumn: undefined,
        filteringValue: undefined,
      }}
      onSubmit={addColumnHandler}
    />
  );
}
